export interface StrikeReportRow {
  id: string;
  label: string;
  values: { [key: string]: number | null };
  children?: StrikeReportRow[];
}

const mergeBreakdownForStrike = (strike: number, reports: any[]): StrikeReportRow | null => {
  const netValues: { [key: string]: number | null } = {};
  let callChild: StrikeReportRow | undefined;
  let putChild: StrikeReportRow | undefined;

  for (const report of reports) {
    const expiry: string = report.expiry_date;
    const breakdown = report.breakdown.find((bd: any) => bd.strike === strike);
    if (!breakdown) continue;

    if (breakdown.net) {
      if (breakdown.net.total !== undefined) {
        netValues[expiry] = breakdown.net.total;
      }
      if (breakdown.net.exchange) {
        for (const exchange in breakdown.net.exchange) {
          const exData = breakdown.net.exchange[exchange as keyof typeof breakdown.net.exchange];
          if (exData.total !== undefined) {
            netValues[`${expiry}-${exchange}`] = exData.total;
          }
          for (const book in exData.book) {
            const key = `${expiry}-${exchange}-${book}`;
            netValues[key] = exData.book[book as keyof typeof exData.book];
          }
        }
      }
    }

    if (breakdown.call) {
      if (!callChild) {
        callChild = { id: `${strike}-call`, label: 'Call', values: {} };
      }
      if (breakdown.call.total !== undefined) {
        callChild.values[expiry] = breakdown.call.total;
      }
      if (breakdown.call.exchange) {
        for (const exchange in breakdown.call.exchange) {
          const exData = breakdown.call.exchange[exchange as keyof typeof breakdown.call.exchange];
          if (exData.total !== undefined) {
            callChild.values[`${expiry}-${exchange}`] = exData.total;
          }
          for (const book in exData.book) {
            const key = `${expiry}-${exchange}-${book}`;
            callChild.values[key] = exData.book[book as keyof typeof exData.book];
          }
        }
      }
      if (breakdown.call.otc_clients) {
        if (!callChild.children) callChild.children = [];
        for (const client in breakdown.call.otc_clients) {
          const clientData =
            breakdown.call.otc_clients[client as keyof typeof breakdown.call.otc_clients];
          let clientRow = callChild.children.find(
            (row: any) => row.id === `${strike}-call-${client}`,
          );
          if (!clientRow) {
            clientRow = { id: `${strike}-call-${client}`, label: client, values: {} };
            callChild.children.push(clientRow);
          }
          if (clientData.total !== undefined) {
            clientRow.values[`${expiry}-OTC`] = clientData.total;
          }
          for (const book in clientData.book) {
            const key = `${expiry}-OTC-${book}`;
            clientRow.values[key] = clientData.book[book as keyof typeof clientData.book];
          }
        }
      }
    }

    if (breakdown.put) {
      if (!putChild) {
        putChild = { id: `${strike}-put`, label: 'Put', values: {} };
      }
      if (breakdown.put.total !== undefined) {
        putChild.values[expiry] = breakdown.put.total;
      }
      if (breakdown.put.exchange) {
        for (const exchange in breakdown.put.exchange) {
          const exData = breakdown.put.exchange[exchange as keyof typeof breakdown.put.exchange];
          if (exData.total !== undefined) {
            putChild.values[`${expiry}-${exchange}`] = exData.total;
          }
          for (const book in exData.book) {
            const key = `${expiry}-${exchange}-${book}`;
            putChild.values[key] = exData.book[book as keyof typeof exData.book];
          }
        }
      }
      if (breakdown.put.otc_clients) {
        if (!putChild.children) putChild.children = [];
        for (const client in breakdown.put.otc_clients) {
          const clientData =
            breakdown.put.otc_clients[client as keyof typeof breakdown.put.otc_clients];
          let clientRow = putChild.children.find(
            (row: any) => row.id === `${strike}-put-${client}`,
          );
          if (!clientRow) {
            clientRow = { id: `${strike}-put-${client}`, label: client, values: {} };
            putChild.children.push(clientRow);
          }
          if (clientData.total !== undefined) {
            clientRow.values[`${expiry}-OTC`] = clientData.total;
          }
          for (const book in clientData.book) {
            const key = `${expiry}-OTC-${book}`;
            clientRow.values[key] = clientData.book[book as keyof typeof clientData.book];
          }
        }
      }
    }
  }

  if (Object.keys(netValues).length > 0 || callChild || putChild) {
    const children: StrikeReportRow[] = [];
    if (callChild) children.push(callChild);
    if (putChild) children.push(putChild);

    return {
      id: String(strike),
      label: String(strike),
      values: netValues,
      children: children.length > 0 ? children : undefined,
    };
  }
  return null;
};

export const getStrikeReportRows = (data: any): StrikeReportRow[] => {
  if (!data) return [];
  const strikes: number[] = data.strikes;
  const reports = data.report;
  return strikes.map((strike) => {
    const breakdownRow = mergeBreakdownForStrike(strike, reports);
    if (breakdownRow) {
      return breakdownRow;
    }
    return { id: String(strike), label: String(strike), values: {} };
  });
};
